import { Box, InputLabel, TextField } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import {
  formatUnitValue,
  parseUnitValue,
  toEditableUnitValue,
} from "../../../utils/unitValue";

type Props = {
  label: string;
  value?: number;
  prefix?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  onChange: (value: number) => void;
  onBlur?: (value: number) => void;
  InputProps?: any;
};

const UnitValueInput: React.FC<Props> = ({
  label,
  value = 0,
  prefix,
  disabled,
  error,
  helperText,
  onChange,
  onBlur,
  InputProps,
}) => {
  const currency = prefix || "BRL";
  const [draft, setDraft] = useState(() => formatUnitValue(value, currency));
  const committedRef = useRef(value);
  const valuePropRef = useRef(value);
  const editingRef = useRef(false);

  useEffect(() => {
    if (editingRef.current) return;
    if (value === valuePropRef.current) {
      setDraft(formatUnitValue(committedRef.current, currency));
      return;
    }
    valuePropRef.current = value;
    committedRef.current = value;
    setDraft(formatUnitValue(value, currency));
  }, [value, currency]);

  const finishEditing = () => {
    if (!editingRef.current) return;
    editingRef.current = false;
    const parsed = parseUnitValue(draft);
    committedRef.current = parsed;
    onChange(parsed);
    setDraft(formatUnitValue(parsed, currency));
    onBlur?.(parsed);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    editingRef.current = true;
    setDraft(toEditableUnitValue(committedRef.current));
    e.target.select();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      finishEditing();
    }
  };

  return (
    <Box width={"100%"}>
      <InputLabel sx={{ fontSize: 13 }} error={error}>
        {label}
      </InputLabel>
      <TextField
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={handleFocus}
        onBlur={finishEditing}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        error={error}
        helperText={helperText}
        color={"info"}
        size="small"
        fullWidth
        InputProps={InputProps}
      />
    </Box>
  );
};

export default UnitValueInput;
